// Who may do what to an alert. The server answers it per row (`can`); these rules only explain a refusal and stand in
// for a row without `can`. The backend enforces the same (alert.ack, alert.close.any, scope).
import type { Alert, AlertType, Me, Permission, Scope } from '@lib/api';

/** Types that clear by themselves when the condition does (a reader reconnects, a cylinder ships). */
const SELF_CLEARING = new Set<AlertType>(['GATEWAY_OFFLINE', 'READER_ERROR', 'CLOCK_UNSYNCED', 'TEST_DUE', 'SEEN_WITHOUT_SHIPMENT', 'SHIPMENT_MISSING']);
export const clearsItself = (t: AlertType) => SELF_CLEARING.has(t);

/** Why the signed-in person may not act on an alert: no permission, a system alert, someone else's place. */
export type AlertBlock = 'role' | 'national' | 'elsewhere' | null;

export interface AlertActor {
  canAck: boolean;
  /** may close a type that clears by itself too (an admin) */
  closeAny: boolean;
  scope: Scope;
}
export const actorOf = (me: Me, can: (p: Permission) => boolean): AlertActor => ({
  canAck: can('alert.ack'),
  closeAny: can('alert.close.any'),
  scope: me.scope ?? { kind: 'national' },
});

/**
 * A national account acts on any alert, a province account on what it lists (its province), an operator only at its
 * own location (it reads its network). An alert without a place is national-only.
 */
export function alertBlock(a: Pick<Alert, 'location'>, actor: AlertActor): AlertBlock {
  if (!actor.canAck) return 'role';
  if (actor.scope.kind === 'national') return null;
  if (!a.location) return 'national';
  if (actor.scope.kind === 'province') return null;
  return a.location.id === actor.scope.locationId ? null : 'elsewhere';
}

export interface AlertAllowed { block: AlertBlock; ack: boolean; close: boolean }

/** What the person may do with this alert now: the server's `can` when it sent one. */
export function alertAllowed(a: Alert, actor: AlertActor): AlertAllowed {
  const block = alertBlock(a, actor);
  if (a.can) return { block, ack: a.can.ack, close: a.can.close };
  return {
    block,
    ack: block === null && a.state === 'needs_action',
    /** "Tutup": a type that cannot clear by itself; with alert.close.any any type */
    close: block === null && a.state !== 'resolved' && (!clearsItself(a.type) || actor.closeAny),
  };
}
